import React, {
  ReactNode, useEffect, useMemo, useState,
} from 'react';
import { Card, Form, Spinner } from 'react-bootstrap';
import { CircleMarker } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';

import InteractiveMap from 'components/InteractiveMap';
import CanvasHeatLayer from 'components/InteractiveMap/CanvasHeatLayer';
import { ALERTS_PANE } from 'components/InteractiveMap/MapPanes';
import {
  AgroOverviewLayersState,
  AgroOverviewLocation,
  AgroOverviewMapVariable,
  MapValuePoint,
  RiskLevel,
} from 'types/Meteorology/Agro/Overview/Dashboard';
import {
  RISK_COLOR,
  removerSufixoUf,
} from 'helper/Meteorology/Agro/Overview/locationsTable';
import {
  colorScaleForVariable,
  derivedNeutralHeatConfig,
  isLightScaleColor,
  scaleRangeLabel,
  scaleStopLabel,
} from 'helper/Meteorology/Agro/Overview/colorScale';
import {
  MapPeriod,
  formatMapPeriod,
} from 'helper/Meteorology/Agro/Overview/mapPeriod';
import {
  scaleHeading,
  variableInfo,
} from 'helper/Meteorology/Agro/Overview/variableInfo';
import {
  selectAgroOverviewLayers,
  selectAgroOverviewSelectedVariable,
  setSelectedVariable,
  toggleLayer,
} from 'redux/reducers/meteorologyAgroOverviewDashboardReducer';
import BrazilStatesLayer from 'components/Meteorology/Agro/BrazilStatesLayer';
import BrazilCountryLayer from '../BrazilCountryLayer';
import DashboardDropdown from '../Dropdown';
import InfoModal from '../InfoModal';

import './index.scss';

const ESRI_TILE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const ESRI_TILE_ATTRIBUTION = 'Tiles &copy; Esri';

export default function VariableMap({
  locations,
  valuePoints,
  variables,
  period,
  loading,
  error,
}: {
  locations: AgroOverviewLocation[];
  valuePoints: MapValuePoint[];
  /** Opções do seletor — dinâmicas via rota mapa/variaveis (DECISÃO-8). */
  variables: AgroOverviewMapVariable[];
  /** Datas DD/MM/YYYY da rota do mapa; um dia só (ARM) exibe "Data: X". */
  period: MapPeriod | null;
  loading: boolean;
  /** Aviso de falha do mapa: ocupa o lugar do mapa, e o seletor de variável continua. */
  error?: ReactNode;
}) {
  const dispatch = useDispatch();
  const selectedVariable = useSelector(selectAgroOverviewSelectedVariable);
  const layers = useSelector(selectAgroOverviewLayers);

  const [showVariableInfo, setShowVariableInfo] = useState(false);
  const hasError = Boolean(error);

  // Em falha o InfoModal não é montado; sem zerar, ele reabriria sozinho quando o mapa voltasse.
  useEffect(() => {
    if (hasError) setShowVariableInfo(false);
  }, [hasError]);

  const handleToggle = (key: keyof AgroOverviewLayersState) => () => dispatch(toggleLayer(key));

  const variableOptions = useMemo(
    () => variables.map((v) => ({ value: v.nome, label: v.label })),
    [variables],
  );
  const selectedVariableMeta = variables.find((v) => v.nome === selectedVariable);
  const unidade = selectedVariableMeta?.unidade ?? '';

  // ADENDO-4: config oficial do BD tem precedência (escala idêntica ao BH
  // Sequencial); fallback = placeholder ARM / escala neutra (DECISÃO-7).
  const scale = useMemo(
    () => colorScaleForVariable(
      selectedVariable,
      valuePoints.map((p) => p.valor),
      selectedVariableMeta?.config,
    ),
    [selectedVariable, valuePoints, selectedVariableMeta],
  );

  // Mancha suavizada (mesmo motor do BH Sequencial). Sem config oficial,
  // deriva uma neutra do domínio real (suporta negativos via fixedMin).
  const heatPoints = useMemo(
    () => valuePoints.map(
      (p) => [p.latitude, p.longitude, p.valor] as [number, number, number],
    ),
    [valuePoints],
  );
  const heatConfig = useMemo(
    () => selectedVariableMeta?.config
      ?? derivedNeutralHeatConfig(valuePoints.map((p) => p.valor))
      ?? undefined,
    [selectedVariableMeta, valuePoints],
  );

  const info = variableInfo(selectedVariable);

  const variableDropdown = (
    <DashboardDropdown
      label="Variável exibida"
      value={selectedVariable}
      onChange={(next) => dispatch(setSelectedVariable(next))}
      options={variableOptions}
      onInfo={error ? undefined : () => setShowVariableInfo(true)}
    />
  );

  // Mapa em falha (#2009, item 2): o aviso ocupa o lugar do mapa, mas o seletor segue
  // à mão para trocar de variável (a escolha fica no Redux e só um recarregamento a trocava).
  // Sem a lista de variáveis (a rota delas falhou) não há o que escolher.
  if (error) {
    return (
      <Card className="agro-overview-variable-map-card h-100">
        <Card.Body className="d-flex flex-column gap-3">
          {variableOptions.length > 0 && <div>{variableDropdown}</div>}
          {error}
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="agro-overview-variable-map-card h-100">
      <Card.Body className="d-flex flex-column gap-3">
        <div className="d-flex gap-4 align-items-end">
          {variableDropdown}
          <div className="flex-grow-1">
            {/* `unidade` continua crua de propósito: ela também monta as faixas
                do modal (`0–1 dias`), que o texto longo deixaria ilegíveis. */}
            <div className="map-label">
              {scaleHeading(selectedVariable, unidade)}
            </div>
            <div
              className="escala-bar"
              style={{ gridTemplateColumns: `repeat(${scale.stops.length || 1}, 1fr)` }}
            >
              {scale.stops.map((f) => (
                <span
                  key={`${f.min}-${f.max}`}
                  className={isLightScaleColor(f.color) ? 'on-light-scale-color' : undefined}
                  style={{ backgroundColor: f.color }}
                >
                  {scaleStopLabel(f)}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="agro-overview-map-wrapper position-relative">
          <InteractiveMap
            tileUrl={ESRI_TILE_URL}
            tileAttribution={ESRI_TILE_ATTRIBUTION}
            preferCanvas
            markers={layers.localidades ? locations.map((l) => ({
              locationId: l.id,
              position: [l.latitude, l.longitude] as [number, number],
              label: l.estado
                ? `${removerSufixoUf(l.nome, l.estado)} / ${l.estado}`
                : l.nome,
            })) : []}
          >
            {/* Mancha SUAVIZADA (decisão 10/08/2026): mesmo motor do BH
                Sequencial, com a config oficial do BD quando existir.
                Some ao desligar "Exibir variável" (#1924), para liberar a
                leitura das bolinhas de risco sob cada pin. Fica sempre por
                baixo das divisas e do alerta — a ordem vem dos panes do
                InteractiveMap, não da ordem destes filhos. */}
            {layers.exibirVariavel && (
              <CanvasHeatLayer points={heatPoints} config={heatConfig} />
            )}
            {/* Alerta de Estresse (decisão 10/08/2026): círculo na cor do
                risco sob o pin de cada localidade; some com a camada. */}
            {layers.alertas && locations
              .filter((l): l is AgroOverviewLocation & { risco7d: RiskLevel } => (
                l.risco7d !== null
              ))
              .map((l) => (
                <CircleMarker
                  key={`alerta-${l.id}`}
                  center={[l.latitude, l.longitude] as [number, number]}
                  pane={ALERTS_PANE}
                  radius={10}
                  pathOptions={{
                    color: '#ffffff', // tema: fixo (desenhado sobre o mapa)
                    weight: 2,
                    fillColor: RISK_COLOR[l.risco7d],
                    fillOpacity: 0.95,
                  }}
                />
              ))}
            {/* Mutuamente exclusivos: divisas estaduais ja desenham o perimetro
                do pais nas bordas externas dos estados. Sobrepor as duas
                camadas causaria linhas duplicadas. */}
            {layers.limitesEstaduais
              ? <BrazilStatesLayer />
              : <BrazilCountryLayer />}
          </InteractiveMap>

          {loading && (
            <div className="map-loading-overlay">
              <Spinner animation="border" />
            </div>
          )}

          <div className="map-overlay map-overlay-layers">
            <div className="overlay-title">CAMADAS</div>
            <Form.Check
              type="switch"
              id="layer-localidades"
              label="Localidades"
              checked={layers.localidades}
              onChange={handleToggle('localidades')}
            />
            <Form.Check
              type="switch"
              id="layer-alertas"
              label="Alerta de Estresse"
              checked={layers.alertas}
              onChange={handleToggle('alertas')}
            />
            <Form.Check
              type="switch"
              id="layer-limites"
              label="Limites estaduais"
              checked={layers.limitesEstaduais}
              onChange={handleToggle('limitesEstaduais')}
            />
            <Form.Check
              type="switch"
              id="layer-exibir-variavel"
              label="Exibir variável"
              checked={layers.exibirVariavel}
              onChange={handleToggle('exibirVariavel')}
            />
          </div>

          {/* LEGENDA removida do mapa (decisão 10/08/2026) — a escala já
              aparece na barra superior e no modal ⓘ da variável. */}
          <div className="map-period">
            {formatMapPeriod(period)}
          </div>
        </div>
      </Card.Body>

      <InfoModal
        title={selectedVariableMeta?.label ?? 'Variável exibida'}
        show={showVariableInfo}
        onHide={() => setShowVariableInfo(false)}
      >
        {/* Textos por variável em helper/.../variableInfo (#1924). O ARM é a
            única sem a linha de período: usa o dado mais atual, não um período
            fechado (decisão da Bruna em 26/08/2026). */}
        {info.showPeriod && selectedVariableMeta && (
          <p>
            {`Período: ${selectedVariableMeta.periodo}. `}
            {selectedVariableMeta.unidade
              ? `Unidade: ${selectedVariableMeta.unidade}.`
              : ''}
          </p>
        )}
        {info.paragraphs.map((texto) => <p key={texto}>{texto}</p>)}

        <div className="info-section-title">Escala de classificação</div>
        {scale.stops.map((f) => (
          <div key={`${f.min}-${f.max}`} className="info-scale-row">
            <span className="info-scale-dot" style={{ backgroundColor: f.color }} />
            <span>
              <strong>{scaleRangeLabel(f.min, f.max, unidade)}</strong>
              {f.label ? ` — ${f.label}` : ''}
            </span>
          </div>
        ))}
      </InfoModal>
    </Card>
  );
}
